/**
 * @zendir/ui - AstroChart Component
 *
 * Enterprise-grade unified charting component built on ECharts.
 * Follows AstroUXD design patterns with full theme integration.
 *
 * Features:
 * - 15+ chart types
 * - Real-time streaming support
 * - Zoom, pan, brush selection
 * - Annotations (thresholds, regions)
 * - Export (PNG, SVG, CSV)
 * - Crosshair sync across charts
 * - Full accessibility support
 * - Dark/light theme support
 */

import React, {
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useState,
  forwardRef,
  useImperativeHandle,
  memo,
} from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import {
  LineChart,
  BarChart,
  PieChart,
  ScatterChart,
  RadarChart,
  GaugeChart,
  HeatmapChart,
  CandlestickChart,
  BoxplotChart,
  TreemapChart,
  SunburstChart,
  FunnelChart,
  SankeyChart,
  CustomChart,
  GraphChart,
  ParallelChart as EChartsParallelChart,
} from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  DataZoomComponent,
  ToolboxComponent,
  MarkLineComponent,
  MarkAreaComponent,
  MarkPointComponent,
  VisualMapComponent,
  BrushComponent,
  PolarComponent,
  RadarComponent,
  GeoComponent,
  ParallelComponent,
} from "echarts/components";
import { CanvasRenderer, SVGRenderer } from "echarts/renderers";
import type { EChartsOption, ECharts } from "echarts";

import { useTheme } from "../../theme";
import { AstroIcon, type AstroIconName } from "../../core/display/AstroIcon";
import { HeaderIconWithStatus } from "../../core/display/HeaderIconWithStatus";
import { useCardBorderStyle } from "../../context/DisplaySettingsContext";
import {
  createAstroEChartsTheme,
  createAreaGradient,
  getSeriesColor,
} from "./theme";
import type {
  AstroChartProps,
  SeriesData,
  ChartType,
  ChartEventParams,
  DataPoint,
  TimeSeriesPoint,
  ExportFileNameContext,
  ChartStatus,
} from "./types";

// =============================================================================
// Status Shape SVGs (matching DataValue component for consistency)
// =============================================================================

const STATUS_SHAPES: Record<
  ChartStatus,
  (size: number, color: string) => React.ReactNode
> = {
  off: (size, color) => (
    <svg viewBox="0 0 12 12" width={size} height={size}>
      <circle cx="6" cy="6" r="5" fill={color} />
    </svg>
  ),
  standby: (size, color) => (
    <svg viewBox="0 0 12 12" width={size} height={size}>
      <circle cx="6" cy="6" r="5" fill={color} />
    </svg>
  ),
  normal: (size, color) => (
    <svg viewBox="0 0 12 12" width={size} height={size}>
      <circle cx="6" cy="6" r="5" fill={color} />
    </svg>
  ),
  caution: (size, color) => (
    <svg viewBox="0 0 12 12" width={size} height={size}>
      <rect x="1" y="1" width="10" height="10" fill={color} />
    </svg>
  ),
  serious: (size, color) => (
    <svg viewBox="0 0 12 12" width={size} height={size}>
      <polygon points="6,1 11,6 6,11 1,6" fill={color} />
    </svg>
  ),
  critical: (size, color) => (
    <svg viewBox="0 0 12 12" width={size} height={size}>
      <polygon points="6,11 1,2 11,2" fill={color} />
    </svg>
  ),
};

/**
 * Astro UX Status Labels
 * Follows Astro UXDS naming conventions
 * https://www.astrouxds.com/patterns/status-system/
 */
const STATUS_LABELS: Record<ChartStatus, string> = {
  off: "Off",
  standby: "Standby",
  normal: "Normal",
  caution: "Caution",
  serious: "Serious",
  critical: "Critical",
};

/** Slug for safe filenames: lowercase, alphanumeric and hyphens only */
function slug(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/** Build base export name from title, series names, and chart type */
function derivedExportBaseName(
  title: string | undefined,
  seriesNames: string[],
  chartType: string,
): string {
  const parts: string[] = ["zendir"];
  if (title) parts.push(slug(title));
  else parts.push("chart");
  const seriesSlug = seriesNames
    .slice(0, 4)
    .map(slug)
    .filter(Boolean)
    .join("-");
  if (seriesSlug) parts.push(seriesSlug);
  parts.push(slug(chartType));
  return parts.join("-") || "zendir-chart";
}

// Register ECharts components
echarts.use([
  LineChart,
  BarChart,
  PieChart,
  ScatterChart,
  RadarChart,
  GaugeChart,
  HeatmapChart,
  CandlestickChart,
  BoxplotChart,
  TreemapChart,
  SunburstChart,
  FunnelChart,
  SankeyChart,
  CustomChart,
  GraphChart,
  EChartsParallelChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  DataZoomComponent,
  ToolboxComponent,
  MarkLineComponent,
  MarkAreaComponent,
  MarkPointComponent,
  VisualMapComponent,
  BrushComponent,
  PolarComponent,
  RadarComponent,
  GeoComponent,
  ParallelComponent,
  CanvasRenderer,
  SVGRenderer,
]);

// =============================================================================
// Chart Instance Handle
// =============================================================================

export interface AstroChartHandle {
  /** Get ECharts instance */
  getChart: () => ECharts | undefined;
  /** Resize chart */
  resize: () => void;
  /** Export as image (png, jpeg, svg) */
  exportImage: (
    type?: "png" | "jpeg" | "jpg" | "svg",
    pixelRatio?: number,
  ) => string | undefined;
  /** Export data as CSV */
  exportCSV: () => string;
  /** Suggested export filename from title, series names, and chart type (with optional extension and timestamp) */
  getExportFileName: (extension?: string, includeTimestamp?: boolean) => string;
  /** Append data for real-time updates */
  appendData: (seriesIndex: number, data: DataPoint | TimeSeriesPoint) => void;
  /** Clear all data */
  clearData: () => void;
  /** Dispatch action */
  dispatchAction: (action: unknown) => void;
}

// =============================================================================
// Series Transformer
// =============================================================================

function transformSeriesToECharts(
  series: SeriesData[],
  chartType: ChartType,
  theme: ReturnType<typeof createAstroEChartsTheme>,
): EChartsOption["series"] {
  return series.map((s, index) => {
    const seriesType = s.type || chartType;
    const color = s.color || getSeriesColor(index);

    // Base series config
    const baseSeries: Record<string, unknown> = {
      id: s.id,
      name: s.name,
      type: mapChartType(seriesType),
      data: transformData(s.data, seriesType),
      yAxisIndex: s.yAxisIndex || 0,
      showSymbol: seriesType === "scatter" || s.symbol?.type !== "none",
      smooth: s.smooth ?? (seriesType === "line" || seriesType === "area"),
    };

    // Line/Area specific
    if (seriesType === "line" || seriesType === "area") {
      baseSeries.lineStyle = {
        width: s.lineStyle?.width ?? 2,
        type: s.lineStyle?.type ?? "solid",
        opacity: s.lineStyle?.opacity ?? 1,
      };
      baseSeries.itemStyle = { color };

      if (seriesType === "area") {
        baseSeries.areaStyle = s.areaStyle || {
          opacity: 0.3,
          color: createAreaGradient(color),
        };
      }

      if (s.symbol) {
        baseSeries.symbol = s.symbol.type || "circle";
        baseSeries.symbolSize = s.symbol.size || 6;
      }
    }

    // Bar specific
    if (seriesType === "bar") {
      baseSeries.itemStyle = {
        color,
        borderRadius: [4, 4, 0, 0],
      };
      if (s.stack) {
        baseSeries.stack = s.stack;
      }
    }

    // Pie/Donut specific — theme color, no white border on labels
    if (seriesType === "pie" || seriesType === "donut") {
      baseSeries.radius = seriesType === "donut" ? ["40%", "70%"] : "70%";
      baseSeries.label = {
        show: true,
        formatter: "{b}: {d}%",
        color: theme.textStyle.color,
        textBorderWidth: 0,
        textBorderColor: "transparent",
      };
      baseSeries.emphasis = {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: "rgba(0, 0, 0, 0.5)",
        },
      };
    }

    // Scatter specific
    if (seriesType === "scatter") {
      baseSeries.symbolSize = s.symbol?.size || 10;
      baseSeries.itemStyle = { color };
    }

    // Gauge specific
    if (seriesType === "gauge") {
      baseSeries.detail = {
        formatter: "{value}%",
        fontSize: 20,
        color: theme.textStyle.color,
      };
      baseSeries.axisLine = {
        lineStyle: {
          width: 10,
          color: [
            [0.3, theme.color[1]], // Green
            [0.7, theme.color[3]], // Yellow
            [1, theme.color[2]], // Red
          ],
        },
      };
    }

    return baseSeries;
  });
}

function mapChartType(type: ChartType): string {
  switch (type) {
    case "area":
      return "line";
    case "donut":
      return "pie";
    default:
      return type;
  }
}

function transformData(data: SeriesData["data"], type: ChartType): unknown[] {
  if (!data || data.length === 0) return [];

  // Handle array of numbers
  if (typeof data[0] === "number") {
    return data as number[];
  }

  // Handle [x, y] tuples
  if (Array.isArray(data[0])) {
    return data;
  }

  // Handle DataPoint or TimeSeriesPoint
  const firstItem = data[0] as DataPoint | TimeSeriesPoint;

  if ("time" in firstItem) {
    // TimeSeriesPoint
    return (data as TimeSeriesPoint[]).map((d) => [d.time, d.value]);
  }

  // For pie/donut charts, ECharts expects { name, value } format
  if (type === "pie" || type === "donut") {
    return (data as DataPoint[]).map((d) => ({
      name: String(d.x),
      value: d.y,
      // Apply color via itemStyle if specified
      ...(d.color ? { itemStyle: { color: d.color } } : {}),
      // Preserve any additional metadata
      ...(d.meta || {}),
    }));
  }

  // For scatter charts, preserve per-point color as itemStyle
  if (type === "scatter") {
    return (data as DataPoint[]).map((d) => ({
      value: [d.x, d.y],
      ...(d.color ? { itemStyle: { color: d.color } } : {}),
      ...(d.label ? { name: d.label } : {}),
    }));
  }

  // DataPoint for other chart types
  return (data as DataPoint[]).map((d) => [d.x, d.y]);
}

// =============================================================================
// Options Builder
// =============================================================================

function buildEChartsOptions(
  props: AstroChartProps,
  theme: ReturnType<typeof createAstroEChartsTheme>,
  hideTitle = false,
): EChartsOption {
  const {
    type,
    series,
    title,
    subtitle,
    xAxis,
    yAxis,
    tooltip,
    legend,
    zoom,
    annotations,
    loading,
    compactAxes = true, // Default: hide axis names for more chart space
  } = props;

  // Hide title when using custom header (transparent theme with icon)
  const showEChartsTitle = title && !hideTitle;

  // Note: When compactAxes is true, axis names are hidden from the chart
  // but developers can access xAxis.name and yAxis.name via props for custom tooltips

  const isPolar = type === "radar";
  const isPie = type === "pie" || type === "donut";
  const isGauge = type === "gauge";
  const isSankey = type === "sankey";
  const isTreemap = type === "treemap" || type === "sunburst";
  const isGraph =
    props.echartsOptions?.series &&
    Array.isArray(props.echartsOptions.series) &&
    props.echartsOptions.series.some(
      (s: unknown) => (s as { type?: string })?.type === "graph",
    );

  // Detect 3D charts (they use grid3D, xAxis3D, etc. - should NOT have 2D axes)
  const is3D = !!(
    props.echartsOptions?.grid3D ||
    props.echartsOptions?.xAxis3D ||
    props.echartsOptions?.globe ||
    (props.echartsOptions?.series &&
      Array.isArray(props.echartsOptions.series) &&
      props.echartsOptions.series.some((s: unknown) => {
        const seriesType = (s as { type?: string })?.type || "";
        return seriesType.includes("3D") || seriesType.includes("3d");
      }))
  );

  // Charts that don't need cartesian grid/axes
  const needsGrid =
    !isPolar &&
    !isPie &&
    !isGauge &&
    !isSankey &&
    !isTreemap &&
    !isGraph &&
    !is3D &&
    !props.hideAxes;

  // Calculate layout based on title and legend position
  // Default legend to bottom for better chart space utilization
  // When using custom header (hideTitle=true), we still need top spacing for the header
  const hasTitle = hideTitle ? true : !!title;
  const hasSubtitle = hideTitle ? false : !!subtitle;
  const legendPosition = legend?.position ?? "bottom"; // Default to bottom
  const legendAtTop = legendPosition === "top";
  const legendAtBottom = legendPosition === "bottom";
  const showLegend = legend?.show !== false;

  // Build options
  const options: EChartsOption = {
    // Title - positioned to avoid legend overlap (hidden when using custom header)
    title: showEChartsTitle
      ? {
          text: title,
          subtext: subtitle,
          left: "center",
          top: legendAtTop && showLegend ? 35 : 10,
          textStyle: {
            ...theme.title.textStyle,
            fontWeight:
              (theme.title.textStyle.fontWeight as "normal" | "bold") ??
              "normal",
          },
          subtextStyle: theme.title.subtextStyle,
        }
      : undefined,

    // Tooltip with improved contrast
    // When compactAxes is true, axis names are shown in the axisPointer label on hover
    tooltip:
      tooltip?.enabled !== false
        ? ({
            trigger: tooltip?.trigger || (isPie ? "item" : "axis"),
            axisPointer: {
              type:
                tooltip?.crosshair === true || tooltip?.crosshair === "both"
                  ? "cross"
                  : "line",
              lineStyle: theme.axisPointer.lineStyle,
              crossStyle: theme.axisPointer.crossStyle,
              // Show axis value labels on hover (includes axis name context)
              label: compactAxes
                ? {
                    show: true,
                    backgroundColor: theme.tooltip.backgroundColor,
                    borderColor: theme.tooltip.borderColor,
                    color: theme.tooltip.textStyle.color,
                    fontSize: 11,
                    padding: [4, 8],
                    // Add axis name prefix to the label when in compact mode
                    formatter: (params: {
                      axisDimension: string;
                      axisIndex: number;
                      value: number | string;
                    }) => {
                      const axisName =
                        params.axisDimension === "x"
                          ? xAxis?.name
                          : Array.isArray(yAxis)
                            ? yAxis[params.axisIndex]?.name
                            : yAxis?.name;
                      const value =
                        typeof params.value === "number"
                          ? params.value.toLocaleString()
                          : params.value;
                      return axisName ? `${axisName}: ${value}` : String(value);
                    },
                  }
                : undefined,
            },
            backgroundColor: theme.tooltip.backgroundColor,
            borderColor: theme.tooltip.borderColor,
            borderWidth: theme.tooltip.borderWidth,
            textStyle: {
              ...theme.tooltip.textStyle,
              fontSize: 14, // AstroUXDS minimum 14pt
            },
            extraCssText: theme.tooltip.extraCssText,
            confine: tooltip?.confine ?? true,
            formatter: tooltip?.formatter as unknown as undefined,
          } as any)
        : undefined,

    // Legend - ultra-compact styling for maximum chart space
    legend: showLegend
      ? {
          show: true,
          type: "scroll",
          orient: legend?.orient || "horizontal",
          left:
            legend?.position === "left"
              ? "left"
              : legend?.position === "right"
                ? "right"
                : "center",
          top: legendAtTop ? 4 : undefined,
          bottom: legendAtBottom ? 2 : undefined,
          padding: [2, 6],
          itemGap: 8,
          itemWidth: 12,
          itemHeight: 6,
          textStyle: {
            ...theme.legend.textStyle,
            fontSize: 10, // Ultra-compact for maximum chart space
          },
          inactiveColor: theme.legend.inactiveColor,
          selectedMode: legend?.interactive !== false,
        }
      : { show: false },

    // Grid - minimal spacing for maximum chart plotting area
    // When compactAxes is true, axis names are hidden so we need minimal padding
    // containLabel: true ensures axis tick labels don't get cut off
    // Extra space only needed for slider zoom (inside zoom is invisible)
    grid: needsGrid
      ? {
          left: compactAxes ? 8 : 40,
          right:
            Array.isArray(yAxis) && yAxis.length > 1
              ? compactAxes
                ? 8
                : 40
              : 8,
          top: hasTitle
            ? hasSubtitle
              ? 50
              : 36
            : legendAtTop && showLegend
              ? 26
              : 4,
          bottom:
            (legendAtBottom && showLegend ? 26 : 16) +
            (zoom?.type === "slider" || zoom?.type === "both" ? 26 : 0),
          containLabel: true,
        }
      : undefined,

    // X Axis - compact font sizes for more chart space
    // In compact mode, hide axis name (shown in tooltip instead)
    xAxis: needsGrid
      ? {
          type: (xAxis?.type || "time") as
            | "value"
            | "category"
            | "time"
            | "log",
          name: compactAxes ? undefined : xAxis?.name,
          nameLocation: xAxis?.nameLocation || "middle",
          nameGap: 18,
          nameTextStyle: {
            ...theme.xAxis.nameTextStyle,
            fontSize: 10, // Ultra-compact for maximum chart space
            fontWeight: 500,
          },
          min: xAxis?.min,
          max: xAxis?.max,
          axisLine: {
            show: xAxis?.showLine !== false,
            lineStyle: theme.xAxis.axisLine.lineStyle,
          },
          axisTick: {
            lineStyle: theme.xAxis.axisTick.lineStyle,
          },
          axisLabel: {
            ...theme.xAxis.axisLabel,
            fontSize: 10, // Ultra-compact for maximum chart space
            formatter: xAxis?.labelFormatter as unknown as undefined,
            // Prevent label overlap - auto-hide overlapping labels
            hideOverlap: true,
            // Rotate labels slightly if needed for better fit
            rotate: 0,
            // Show fewer labels when space is tight
            interval: "auto",
          },
          splitLine: {
            show: xAxis?.showGrid !== false,
            lineStyle: {
              ...theme.xAxis.splitLine.lineStyle,
              type:
                (theme.xAxis.splitLine.lineStyle.type as
                  | "solid"
                  | "dashed"
                  | "dotted") ?? "solid",
            },
          },
          inverse: xAxis?.inverse,
        }
      : undefined,

    // Y Axis - ultra-compact font sizes for maximum chart space
    // In compact mode, hide axis name (shown in tooltip instead)
    yAxis: (needsGrid
      ? (Array.isArray(yAxis) ? yAxis : [yAxis || {}]).map((axis, i) => ({
          type: (axis?.type || "value") as
            | "value"
            | "category"
            | "time"
            | "log",
          name: compactAxes ? undefined : axis?.name,
          nameLocation: axis?.nameLocation || "middle",
          nameGap: 28,
          nameTextStyle: {
            ...theme.yAxis.nameTextStyle,
            fontSize: 10, // Ultra-compact for maximum chart space
            fontWeight: 500,
          },
          position: axis?.position || (i === 0 ? "left" : "right"),
          min: axis?.min,
          max: axis?.max,
          splitNumber: axis?.splitNumber,
          axisLine: {
            show: axis?.showLine !== false,
            lineStyle: theme.yAxis.axisLine.lineStyle,
          },
          axisTick: {
            lineStyle: theme.yAxis.axisTick.lineStyle,
          },
          axisLabel: {
            ...theme.yAxis.axisLabel,
            fontSize: 10, // Ultra-compact for maximum chart space
            formatter: axis?.labelFormatter
              ? (axis.labelFormatter as unknown as undefined)
              : axis?.unit
                ? (v: number) => `${v}${axis.unit}`
                : undefined,
          },
          splitLine: {
            show: axis?.showGrid !== false,
            lineStyle: theme.yAxis.splitLine.lineStyle,
          },
          inverse: axis?.inverse,
        }))
      : undefined) as EChartsOption["yAxis"],

    // Radar (for radar charts) - transparent background for glassmorphic look
    radar: isPolar
      ? {
          indicator:
            series[0]?.data?.map((d: unknown) => {
              const point = d as DataPoint;
              return {
                name: point.label || point.category || String(point.x),
                max: 100,
              };
            }) || [],
          splitArea: {
            show: true,
            areaStyle: {
              color: [
                "transparent",
                "transparent",
                "transparent",
                "transparent",
                "transparent",
              ],
            },
          },
          splitLine: {
            lineStyle: {
              color: theme.xAxis.splitLine.lineStyle.color,
              opacity: 0.4,
            },
          },
          axisLine: {
            lineStyle: {
              color: theme.xAxis.axisLine.lineStyle.color,
              opacity: 0.4,
            },
          },
          axisName: {
            color: theme.xAxis.axisLabel.color,
            fontSize: 12,
          },
        }
      : undefined,

    // Data Zoom - inside zoom enabled by default for mouse wheel/pinch
    // Use zoom={{ enabled: false }} to disable, or zoom={{ type: 'slider' }} for slider
    dataZoom:
      zoom?.enabled === false
        ? undefined
        : [
            // Inside zoom (mouse wheel / pinch) - ENABLED BY DEFAULT
            ...(zoom?.type === "inside" || zoom?.type === "both" || !zoom?.type
              ? [
                  {
                    type: "inside" as const,
                    xAxisIndex:
                      zoom?.axis === "x" || zoom?.axis === "both" || !zoom?.axis
                        ? 0
                        : undefined,
                    yAxisIndex:
                      zoom?.axis === "y" || zoom?.axis === "both"
                        ? 0
                        : undefined,
                    start: zoom?.start ?? 0,
                    end: zoom?.end ?? 100,
                    minSpan: zoom?.minSpan,
                    maxSpan: zoom?.maxSpan,
                    // Smooth zoom animation
                    throttle: 50,
                  },
                ]
              : []),
            // Slider zoom (only when explicitly requested)
            ...(zoom?.type === "slider" || zoom?.type === "both"
              ? [
                  {
                    type: "slider" as const,
                    show: true,
                    xAxisIndex:
                      zoom?.axis === "x" || zoom?.axis === "both"
                        ? 0
                        : undefined,
                    yAxisIndex:
                      zoom?.axis === "y" || zoom?.axis === "both"
                        ? 0
                        : undefined,
                    start: zoom?.start ?? 0,
                    end: zoom?.end ?? 100,
                    minSpan: zoom?.minSpan,
                    maxSpan: zoom?.maxSpan,
                    backgroundColor: theme.dataZoom.backgroundColor,
                    borderColor: theme.dataZoom.borderColor,
                    fillerColor: theme.dataZoom.fillerColor,
                    handleStyle: { color: theme.dataZoom.handleColor },
                    textStyle: theme.dataZoom.textStyle,
                    height: 25,
                    bottom: 10,
                  },
                ]
              : []),
          ],

    // Toolbox (export) – always use custom dropdown for consistent UI
    toolbox: props.export?.enabled
      ? (() => {
          return {
            show: false, // Hide ECharts toolbox, use custom export button instead
            right: 20,
            top: 10,
            feature: {
              dataView: props.export.formats?.includes("json")
                ? {
                    show: true,
                    readOnly: true,
                  }
                : undefined,
            },
            iconStyle: {
              borderColor: theme.textStyle.color,
            },
          };
        })()
      : undefined,

    // Series
    series: transformSeriesToECharts(series, type, theme),

    // Animation
    animation: !loading,
    animationDuration: 300,
    animationEasing: "cubicOut",
  };

  // Add mark lines (thresholds)
  if (annotations?.markLines && options.series) {
    const seriesArray = options.series as Array<Record<string, unknown>>;
    if (seriesArray.length > 0) {
      seriesArray[0].markLine = {
        silent: true,
        symbol: "none",
        data: annotations.markLines.data.map((line) => ({
          ...line,
          lineStyle: {
            color: line.color || theme.color[2],
            type: line.lineStyle?.type || "dashed",
            width: line.lineStyle?.width || 1,
          },
          label: {
            show: !!line.label,
            formatter: line.label,
            position: "middle", // center of line so label doesn't overlap y-axis at bottom
            distance: 8,
            color: theme.textStyle.color,
            fontWeight: 600,
            fontSize: 11,
            textBorderWidth: 0,
            textBorderColor: "transparent",
            backgroundColor: theme.tooltip.backgroundColor,
            borderColor: line.color || theme.color[2],
            borderWidth: 1,
            borderRadius: 4,
            padding: [2, 6],
          },
        })),
      };
    }
  }

  // Add mark areas (regions) — label style: no white border, crisp and slightly faded; optional icon
  if (annotations?.markAreas && options.series) {
    const seriesArray = options.series as Array<Record<string, unknown>>;
    const axisLabelColor =
      (theme.xAxis as { axisLabel?: { color?: string } })?.axisLabel?.color ??
      theme.textStyle?.color ??
      "#94a3b8";
    const fadedColor = axisLabelColor.startsWith("rgba")
      ? axisLabelColor
      : `${axisLabelColor}cc`;
    const defaultLabelStyle = {
      show: true,
      color: fadedColor,
      fontSize: 11,
      fontWeight: "500" as const,
      textBorderWidth: 0,
      textBorderColor: "transparent",
      formatter: (params: { name?: string; value?: unknown }) => {
        const name =
          (params?.name ?? "") ||
          (typeof params?.value === "string" ? params.value : "");
        if (!name) return "";
        const icon = name.toLowerCase().includes("eclipse") ? "\u25D0 " : ""; // ◐ symbol for eclipse
        return icon ? `${icon}${name}` : name;
      },
    };
    if (seriesArray.length > 0) {
      seriesArray[0].markArea = {
        silent: true,
        data: annotations.markAreas.data.map(
          (
            pair: [
              { xAxis?: number | string; yAxis?: number; name?: string },
              { xAxis?: number | string; yAxis?: number },
            ],
          ) => {
            const [start, end] = pair;
            return [
              {
                ...start,
                label: {
                  ...defaultLabelStyle,
                  formatter: defaultLabelStyle.formatter,
                },
              },
              end,
            ];
          },
        ),
        itemStyle: annotations.markAreas.itemStyle,
      };
    }
  }

  // Merge custom options
  if (props.echartsOptions) {
    return mergeOptions(options, props.echartsOptions);
  }

  return options;
}

function mergeOptions(
  base: EChartsOption,
  custom: Partial<EChartsOption>,
): EChartsOption {
  // Smart series merging logic
  let mergedSeries = base.series;

  if (custom.series) {
    const baseSeries =
      (base.series as Array<{ type?: string; data?: unknown[] }>) || [];
    const customSeries = custom.series as unknown[];

    // Check if all base series are empty (no data or empty data array)
    const allBaseSeriesEmpty =
      baseSeries.length === 0 ||
      baseSeries.every((s) => {
        if (!s) return true;
        if (!s.data) return true;
        if (Array.isArray(s.data) && s.data.length === 0) return true;
        return false;
      });

    // Single full override: presets (e.g. GaugeChart) pass one full series config; use it only so we don't render two gauges
    const baseLen = baseSeries.length;
    const customLen = customSeries.length;
    if (baseLen === 1 && customLen === 1) {
      const b = baseSeries[0];
      const c = customSeries[0] as { type?: string };
      if (b?.type === "gauge" && c?.type === "gauge") {
        mergedSeries = customSeries as EChartsOption["series"];
      } else if (allBaseSeriesEmpty) {
        mergedSeries = customSeries as EChartsOption["series"];
      } else {
        mergedSeries = [
          ...baseSeries,
          ...customSeries,
        ] as EChartsOption["series"];
      }
    } else if (allBaseSeriesEmpty) {
      mergedSeries = customSeries as EChartsOption["series"];
    } else {
      mergedSeries = [
        ...baseSeries,
        ...customSeries,
      ] as EChartsOption["series"];
    }
  }

  return {
    ...base,
    ...custom,
    series: mergedSeries,
  };
}

// =============================================================================
// Main Component
// =============================================================================

export const AstroChart = memo(
  forwardRef<AstroChartHandle, AstroChartProps>(
    function AstroChart(props, ref) {
      const {
        width = "100%",
        height = 400,
        loading = false,
        emptyMessage = "No data available",
        className = "",
        style,
        ariaLabel,
        onChartReady,
        onClick,
        onHover,
        onZoomChange,
        onBrushSelect,
        realTime,
        series = [],
        infoTooltip,
        // Header icon/status system
        icon,
        iconStatus,
        statusMessage,
        showStatusBadge = false,
      } = props;

      const chartRef = useRef<ReactEChartsCore>(null);
      const { tokens, mode, theme: themeVariant } = useTheme();

      // Detect transparent theme for glassmorphic styling
      const isTransparentTheme =
        themeVariant === "transparent" ||
        themeVariant === "transparent-bold" ||
        themeVariant === "transparent-minimal";
      const isBoldVariant = themeVariant === "transparent-bold";
      const isMinimalVariant = themeVariant === "transparent-minimal";

      // Get card border style based on accentBorders setting
      const cardBorderStyle = useCardBorderStyle(tokens, isTransparentTheme);

      // Glass accent colors for bold/minimal themes (matching Container)
      const glassAccentColor = tokens.colors.accent.primary;
      const glassAccentMuted = `${glassAccentColor}66`;

      const headingConfig = tokens.layout?.card?.heading ?? {
        iconSize: 20,
        iconSizeCompact: 16,
        titleFontSize: "1.125rem",
        titleFontSizeChart: "0.875rem",
        titleFontWeight: 500,
        gap: 8,
      };
      const chartTitleFontSize =
        headingConfig.titleFontSizeChart ?? headingConfig.titleFontSize;

      // Generate theme - uses official AstroUXDS colors for 'astro' theme, Zendir colors for others
      const theme = useMemo(
        () => createAstroEChartsTheme(tokens, mode, themeVariant),
        [tokens, mode, themeVariant],
      );

      // Build options - hide ECharts title whenever we show custom header (top-left, all themes)
      const hideEChartsTitle = !!(icon || props.title);
      const options = useMemo(
        () => buildEChartsOptions(props, theme, hideEChartsTitle),
        [props, theme, hideEChartsTitle],
      );

      // Get chart instance.
      // echarts-for-react types the instance via echarts/core's internal
      // EChartsType, while callers here consume the public `ECharts` type from
      // 'echarts'. They are the same runtime object, so bridge the two nominal
      // declarations at this single boundary.
      const getChart = useCallback((): ECharts | undefined => {
        return chartRef.current?.getEchartsInstance() as unknown as
          | ECharts
          | undefined;
      }, []);

      // Dynamic export filename from title, series names, chart type
      const getExportFileName = useCallback(
        (extension?: string, includeTimestamp = true) => {
          const context: ExportFileNameContext = {
            title: props.title,
            subtitle: props.subtitle,
            seriesNames: series.map((s) => s.name),
            chartType: props.type,
          };
          let base: string;
          if (props.export?.getExportFileName) {
            base = props.export.getExportFileName(context);
          } else if (props.export?.fileName) {
            base = props.export.fileName;
          } else {
            base = derivedExportBaseName(
              props.title,
              context.seriesNames,
              props.type,
            );
          }
          if (includeTimestamp) {
            const d = new Date();
            const ts = d
              .toISOString()
              .slice(0, 19)
              .replace(/[-T:]/g, "")
              .replace(/(\d{8})(\d{6})/, "$1-$2");
            base = `${base}-${ts}`;
          }
          if (extension) base = `${base}.${extension.replace(/^\./, "")}`;
          return base;
        },
        [props.title, props.subtitle, props.type, props.export, series],
      );

      // Expose imperative handle
      useImperativeHandle(
        ref,
        () => ({
          getChart,
          resize: () => getChart()?.resize(),
          getExportFileName,
          exportImage: (type = "png", pixelRatio = 2) => {
            const chart = getChart();
            if (!chart) return undefined;
            const imgType = type === "jpg" ? "jpeg" : type;
            return chart.getDataURL({
              type: imgType as "png" | "jpeg" | "svg",
              pixelRatio,
              backgroundColor: "transparent",
            });
          },
          exportCSV: () => {
            const rows: string[] = [];
            const headers = ["Index", ...series.map((s) => s.name)];
            rows.push(headers.join(","));

            const maxLen = Math.max(...series.map((s) => s.data?.length || 0));
            for (let i = 0; i < maxLen; i++) {
              const row = [i.toString()];
              for (const s of series) {
                const data = s.data as unknown[];
                const value = data?.[i];
                if (typeof value === "number") {
                  row.push(value.toString());
                } else if (Array.isArray(value)) {
                  row.push(value[1]?.toString() || "");
                } else if (value && typeof value === "object") {
                  const point = value as DataPoint | TimeSeriesPoint;
                  row.push(
                    ("value" in point ? point.value : point.y)?.toString() ||
                      "",
                  );
                } else {
                  row.push("");
                }
              }
              rows.push(row.join(","));
            }
            return rows.join("\n");
          },
          appendData: (
            seriesIndex: number,
            data: DataPoint | TimeSeriesPoint,
          ) => {
            const chart = getChart();
            if (!chart) return;

            // Get current data
            const option = chart.getOption();
            const seriesOpt = (option.series as Array<{ data: unknown[] }>)?.[
              seriesIndex
            ];
            if (!seriesOpt) return;

            const currentData = [...(seriesOpt.data || [])];
            const newPoint =
              "time" in data ? [data.time, data.value] : [data.x, data.y];

            currentData.push(newPoint);

            // Apply max points limit
            const maxPoints = realTime?.maxPoints || 1000;
            while (currentData.length > maxPoints) {
              currentData.shift();
            }

            // Update chart
            chart.setOption(
              {
                series: [
                  {
                    data: currentData,
                  },
                ],
              },
              {
                replaceMerge: ["series"],
              },
            );
          },
          clearData: () => {
            const chart = getChart();
            if (!chart) return;

            chart.setOption({
              series: series.map(() => ({ data: [] })),
            });
          },
          dispatchAction: (action: unknown) => {
            getChart()?.dispatchAction(
              action as Parameters<ECharts["dispatchAction"]>[0],
            );
          },
        }),
        [getChart, series, realTime],
      );

      // Zoom preservation: ReactEChartsCore re-renders with notMerge=true whenever
      // `options` changes (which happens on every series-data update). Without this,
      // streaming charts snap back to start=0/end=100 each frame, defeating user
      // pan/zoom.
      //
      // We capture user pan/zoom on the `datazoom` event and replay it after every
      // option update. For streaming time-series we MUST use absolute axis values
      // (startValue/endValue) — preserving percent (start/end) would slide the
      // window forward as new data extends the axis, which feels exactly like a
      // zoom reset from the operator's perspective. Percent is saved as a fallback
      // for category/non-numeric axes where absolute values aren't meaningful.
      //
      // `programmaticRef` suppresses the datazoom event we ourselves dispatch so
      // we don't overwrite the saved range with the values just replayed.
      type ZoomState = {
        start?: number;
        end?: number;
        startValue?: number | string;
        endValue?: number | string;
      };
      const userZoomRef = useRef<ZoomState | null>(null);
      const programmaticRef = useRef(false);

      // Event handlers
      const handleEvents = useMemo(() => {
        const events: Record<string, (params: unknown) => void> = {};

        if (onClick) {
          events.click = (params: unknown) => {
            onClick(params as ChartEventParams);
          };
        }

        if (onHover) {
          events.mouseover = (params: unknown) => {
            onHover(params as ChartEventParams);
          };
        }

        // Always wire datazoom so we can persist zoom state across data updates.
        // `onZoomChange` (if provided) is invoked alongside.
        events.datazoom = (params: unknown) => {
          if (programmaticRef.current) {
            // This event fired as a result of our own dispatchAction replay — ignore
            // so we don't keep overwriting the saved range from the user's last gesture.
            programmaticRef.current = false;
            return;
          }
          type DZParams = {
            start?: number;
            end?: number;
            startValue?: number | string;
            endValue?: number | string;
            batch?: Array<{
              start?: number;
              end?: number;
              startValue?: number | string;
              endValue?: number | string;
            }>;
          };
          const p = params as DZParams;
          const b = p.batch?.[0];
          const start = b?.start ?? p.start ?? 0;
          const end = b?.end ?? p.end ?? 100;
          const startValue = b?.startValue ?? p.startValue;
          const endValue = b?.endValue ?? p.endValue;
          // Only remember user-applied zoom; ignore the no-op full range so a
          // double-click "reset" returns to auto-follow behavior on the next update.
          const isFullRange = start <= 0 && end >= 100;
          userZoomRef.current = isFullRange
            ? null
            : { start, end, startValue, endValue };
          if (onZoomChange) onZoomChange({ start, end });
        };

        if (onBrushSelect) {
          events.brushselected = (params: unknown) => {
            onBrushSelect(
              params as Parameters<NonNullable<typeof onBrushSelect>>[0],
            );
          };
        }

        return events;
      }, [onClick, onHover, onZoomChange, onBrushSelect]);

      // After every options update, replay the user's zoom selection so streaming
      // data doesn't reset their view. Prefer absolute axis values when available
      // so the window stays anchored to the time range the user picked, not a
      // percentage that slides as more data arrives.
      useEffect(() => {
        const saved = userZoomRef.current;
        if (!saved) return;
        const chart = getChart() as
          | (ECharts & { isDisposed?: () => boolean })
          | undefined;
        // Guard against firing into a disposed instance — happens when parents
        // toggle the chart in/out (e.g. `series.length > 0 ? <AstroChart/> : ...`)
        // and React's effect runs while the previous instance is being torn down.
        if (
          !chart ||
          (typeof chart.isDisposed === "function" && chart.isDisposed())
        )
          return;
        const hasAbsolute =
          saved.startValue !== undefined && saved.endValue !== undefined;
        programmaticRef.current = true;
        try {
          chart.dispatchAction(
            hasAbsolute
              ? {
                  type: "dataZoom",
                  startValue: saved.startValue,
                  endValue: saved.endValue,
                }
              : { type: "dataZoom", start: saved.start, end: saved.end },
          );
        } catch {
          // Instance vanished between the isDisposed check and the call — ignore.
          programmaticRef.current = false;
        }
      }, [options, getChart]);

      // Chart ready callback
      const handleChartReady = useCallback(
        (chart: unknown) => {
          onChartReady?.(chart);
        },
        [onChartReady],
      );

      // Custom export dropdown: always show when export is enabled (for consistent UI)
      const exportFormats = props.export?.formats ?? ["png"];
      const useCustomExportDropdown = props.export?.enabled;
      const [exportMenuOpen, setExportMenuOpen] = useState(false);
      const exportMenuRef = useRef<HTMLDivElement>(null);

      // Info tooltip state
      const [infoTooltipOpen, setInfoTooltipOpen] = useState(false);
      const infoTooltipRef = useRef<HTMLDivElement>(null);

      // Close info tooltip on outside click
      useEffect(() => {
        if (!infoTooltip || !infoTooltipOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
          if (
            infoTooltipRef.current &&
            !infoTooltipRef.current.contains(e.target as Node)
          ) {
            setInfoTooltipOpen(false);
          }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
          document.removeEventListener("mousedown", handleClickOutside);
      }, [infoTooltip, infoTooltipOpen]);

      useEffect(() => {
        if (!useCustomExportDropdown || !exportMenuOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
          if (
            exportMenuRef.current &&
            !exportMenuRef.current.contains(e.target as Node)
          ) {
            setExportMenuOpen(false);
          }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
          document.removeEventListener("mousedown", handleClickOutside);
      }, [useCustomExportDropdown, exportMenuOpen]);

      const handleExportFormat = useCallback(
        (format: "png" | "jpeg" | "svg" | "csv") => {
          const chart = getChart();
          if (format === "csv") {
            const rows: string[] = [];
            const headers = ["Index", ...series.map((s) => s.name)];
            rows.push(headers.join(","));
            const maxLen = Math.max(...series.map((s) => s.data?.length || 0));
            for (let i = 0; i < maxLen; i++) {
              const row = [i.toString()];
              for (const s of series) {
                const data = s.data as unknown[];
                const value = data?.[i];
                if (typeof value === "number") row.push(value.toString());
                else if (Array.isArray(value))
                  row.push(value[1]?.toString() || "");
                else if (value && typeof value === "object") {
                  const point = value as DataPoint | TimeSeriesPoint;
                  row.push(
                    ("value" in point ? point.value : point.y)?.toString() ||
                      "",
                  );
                } else row.push("");
              }
              rows.push(row.join(","));
            }
            const csv = rows.join("\n");
            const filename = getExportFileName("csv", true);
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
          } else if (chart) {
            const imgType = format === "jpeg" ? "jpeg" : format;
            const dataUrl = chart.getDataURL({
              type: imgType as "png" | "jpeg" | "svg",
              pixelRatio: props.export?.pixelRatio || 2,
              backgroundColor: props.export?.backgroundColor || "transparent",
            });
            if (dataUrl) {
              const ext = format === "jpeg" ? "jpg" : format;
              const filename = getExportFileName(ext, true);
              const a = document.createElement("a");
              a.href = dataUrl;
              a.download = filename;
              a.click();
            }
          }
          setExportMenuOpen(false);
        },
        [
          getChart,
          series,
          getExportFileName,
          props.export?.pixelRatio,
          props.export?.backgroundColor,
        ],
      );

      // Resize observer (guard so we never resize after unmount/dispose)
      useEffect(() => {
        const chart = getChart();
        if (!chart) return;

        let mounted = true;
        const resizeObserver = new ResizeObserver(() => {
          if (!mounted) return;
          try {
            chart.resize();
          } catch {
            // Chart may be disposed; ignore
          }
        });

        const container = chartRef.current?.ele;
        if (container) {
          resizeObserver.observe(container);
        }

        return () => {
          mounted = false;
          resizeObserver.disconnect();
        };
      }, [getChart]);

      // echarts-gl 3D series require canvas renderer (SVG mode crashes with isSingleCanvas error)
      const needs3DCanvas = useMemo(() => {
        const eo = props.echartsOptions;
        if (!eo) return false;
        if (
          eo.grid3D ||
          eo.xAxis3D ||
          eo.yAxis3D ||
          eo.zAxis3D ||
          eo.globe ||
          eo.geo3D
        )
          return true;
        if (Array.isArray(eo.series)) {
          return eo.series.some((s: any) => {
            const t = s?.type || "";
            return (
              t.includes("3D") ||
              t.includes("3d") ||
              t === "scatter3D" ||
              t === "bar3D" ||
              t === "surface" ||
              t === "lines3D"
            );
          });
        }
        return false;
      }, [props.echartsOptions]);

      // Check if data is empty (consider both series prop and echartsOptions.series)
      const hasSeriesData = series.some((s) => s.data && s.data.length > 0);
      const hasEchartsOptionsSeries =
        props.echartsOptions?.series &&
        (Array.isArray(props.echartsOptions.series)
          ? props.echartsOptions.series.length > 0
          : props.echartsOptions.series !== undefined);
      const hasData = hasSeriesData || hasEchartsOptionsSeries;

      // Status color mapping for header icon
      const getStatusColor = (status: ChartStatus | undefined): string => {
        switch (status) {
          case "critical":
            return tokens.colors.status?.critical || "#ff3838";
          case "serious":
            return tokens.colors.status?.serious || "#ffb302";
          case "caution":
            return tokens.colors.status?.caution || "#fce83a";
          case "normal":
            return tokens.colors.status?.normal || "#56f000";
          case "standby":
            return tokens.colors.status?.standby || "#2dccff";
          case "off":
            return tokens.colors.status?.off || "#a4abb6";
          default:
            return tokens.colors.text.secondary;
        }
      };

      // Check if we need to render the header (has icon or title)
      const hasHeader = !!(icon || props.title);

      // Container styles - glassmorphic for transparent themes (matching Container component)
      // Bold and Minimal themes get special glass styling
      const useGlassMode = isBoldVariant || isMinimalVariant;

      const containerStyle: React.CSSProperties = {
        width: typeof width === "number" ? `${width}px` : width,
        height: typeof height === "number" ? `${height}px` : height,
        borderRadius: tokens.borderRadius.lg,
        padding: tokens.spacing.sm,
        position: "relative",
        overflow: "hidden",
        // Glass mode styling for bold/minimal (matching Container)
        ...(useGlassMode
          ? {
              backgroundColor: "rgba(10, 15, 25, 0.35)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              border: `1px solid ${glassAccentMuted}`,
              borderTop: isBoldVariant
                ? `2px solid ${glassAccentColor}`
                : `1px solid ${glassAccentColor}`,
              transition: "all 0.3s ease-out, box-shadow 0.3s ease-out",
            }
          : isTransparentTheme
            ? {
                // Regular transparent (glass) theme — match top cards (ISS): transparent + blur
                backgroundColor: "transparent",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                ...cardBorderStyle,
              }
            : {
                // Non-transparent themes — use same border as Container (muted) so chart doesn't stand out
                backgroundColor: tokens.colors.background.surface,
                border: `1px solid ${tokens.colors.border.muted}`,
              }),
        ...style,
      };

      // Glass gradient for bold/minimal themes
      const glassGradient = useGlassMode
        ? `linear-gradient(135deg, ${glassAccentColor}10 0%, ${glassAccentColor}03 100%)`
        : undefined;

      // Empty state
      if (!hasData && !loading) {
        return (
          <div
            className={`astro-chart astro-chart--empty ${className}`}
            style={containerStyle}
            role="group"
            aria-roledescription="chart"
            aria-label={ariaLabel || "Empty chart"}
          >
            {/* Glass gradient overlay for bold/minimal themes */}
            {useGlassMode && glassGradient && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: glassGradient,
                  pointerEvents: "none",
                  zIndex: 0,
                }}
              />
            )}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: tokens.colors.text.secondary,
                fontFamily: tokens.typography.fontFamily.primary,
                fontSize: tokens.typography.fontSize.sm,
                position: "relative",
                zIndex: 1,
              }}
            >
              {emptyMessage}
            </div>
          </div>
        );
      }

      const exportImageFormats = exportFormats.filter((f) =>
        ["png", "jpeg", "jpg", "svg"].includes(f),
      );
      const hasCsv = exportFormats.includes("csv");

      // Calculate header height for chart area adjustment
      const _headerHeight = icon && iconStatus ? 32 : 0;

      return (
        <div
          className={`astro-chart ${className}`}
          style={containerStyle}
          role="group"
          aria-roledescription="chart"
          aria-label={ariaLabel || props.title || "Chart"}
        >
          {/* Glass gradient overlay for bold/minimal themes (matching Container) */}
          {useGlassMode && glassGradient && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: glassGradient,
                pointerEvents: "none",
                zIndex: 0,
              }}
            />
          )}

          {/* Header - BOLD variant (matching Container) */}
          {hasHeader && isBoldVariant && (
            <div style={{ position: "relative", zIndex: 1 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: `12px ${tokens.spacing.sm} 0`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: headingConfig.gap,
                  }}
                >
                  {/* Icon with status badge - card heading system */}
                  {icon && iconStatus && (
                    <HeaderIconWithStatus
                      icon={icon as AstroIconName}
                      size={headingConfig.iconSize}
                      status={iconStatus}
                      statusColor={getStatusColor(iconStatus)}
                    />
                  )}
                  {icon && !iconStatus && (
                    <AstroIcon
                      name={icon as AstroIconName}
                      size={headingConfig.iconSize}
                      color={glassAccentColor}
                    />
                  )}
                  {props.title && (
                    <h3
                      style={{
                        margin: 0,
                        fontSize: chartTitleFontSize,
                        fontWeight: headingConfig.titleFontWeight,
                        color: glassAccentColor,
                        letterSpacing: "0.5px",
                        textShadow: `0 0 20px ${glassAccentMuted}`,
                      }}
                    >
                      {props.title}
                    </h3>
                  )}
                </div>
              </div>
              {/* Accent decorative line with status badge */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: `10px ${tokens.spacing.sm} 0`,
                  marginBottom: tokens.spacing.xs,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: "2px",
                    background: `linear-gradient(90deg, ${glassAccentColor} 0%, ${glassAccentMuted} 50%, transparent 100%)`,
                    boxShadow: `0 0 8px ${glassAccentMuted}`,
                  }}
                />
                {/* Status badge at end of line (optional) */}
                {showStatusBadge && iconStatus && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "2px 8px",
                      fontSize: "0.625rem", // 10px in rem
                      fontWeight: 500, // AstroUXDS medium (not 600)
                      letterSpacing: "0.05em",
                      color: getStatusColor(iconStatus),
                      backgroundColor: `${getStatusColor(iconStatus)}20`,
                      border: `1px solid ${getStatusColor(iconStatus)}40`,
                      borderRadius: "3px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {STATUS_SHAPES[iconStatus](6, getStatusColor(iconStatus))}
                    {STATUS_LABELS[iconStatus]}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Header - MINIMAL variant (inline thin line, matching Container) */}
          {hasHeader && isMinimalVariant && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: `${tokens.spacing.xs} ${tokens.spacing.sm} 0`,
                marginBottom: tokens.spacing.xs,
                position: "relative",
                zIndex: 1,
              }}
            >
              {icon && iconStatus && (
                <HeaderIconWithStatus
                  icon={icon as AstroIconName}
                  size={headingConfig.iconSizeCompact}
                  status={iconStatus}
                  statusColor={getStatusColor(iconStatus)}
                  style={{ marginRight: tokens.spacing.sm }}
                />
              )}
              {icon && !iconStatus && (
                <AstroIcon
                  name={icon as AstroIconName}
                  size={headingConfig.iconSizeCompact}
                  color={glassAccentColor}
                  style={{ marginRight: tokens.spacing.sm }}
                />
              )}
              {props.title && (
                <h3
                  style={{
                    margin: 0,
                    fontSize: chartTitleFontSize,
                    fontWeight: headingConfig.titleFontWeight,
                    color: glassAccentColor,
                    textShadow: `0 0 16px ${glassAccentMuted}`,
                    letterSpacing: "0.02em",
                    whiteSpace: "nowrap",
                    marginRight: "12px",
                  }}
                >
                  {props.title}
                </h3>
              )}
              {/* Accent line with status badge */}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: headingConfig.gap,
                  minWidth: 40,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: "1px",
                    background: `linear-gradient(90deg, ${glassAccentColor} 0%, ${glassAccentMuted} 60%, transparent 100%)`,
                  }}
                />
                {/* Status badge at end of line (optional) */}
                {showStatusBadge && iconStatus && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "3px",
                      padding: "1px 6px",
                      fontSize: "0.5625rem", // 9px in rem (micro)
                      fontWeight: 500, // AstroUXDS medium (not 600)
                      letterSpacing: "0.05em",
                      color: getStatusColor(iconStatus),
                      backgroundColor: `${getStatusColor(iconStatus)}15`,
                      border: `1px solid ${getStatusColor(iconStatus)}30`,
                      borderRadius: "2px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {STATUS_SHAPES[iconStatus](5, getStatusColor(iconStatus))}
                    {STATUS_LABELS[iconStatus]}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Header - Regular glass theme (no box/border) */}
          {hasHeader && isTransparentTheme && !useGlassMode && (
            <div
              title={statusMessage}
              style={{
                position: "absolute",
                top: 6,
                left: 8,
                display: "flex",
                alignItems: "center",
                gap: headingConfig.gap,
                zIndex: 10,
              }}
            >
              {icon && iconStatus && (
                <HeaderIconWithStatus
                  icon={icon as AstroIconName}
                  size={headingConfig.iconSizeCompact}
                  status={iconStatus}
                  statusColor={getStatusColor(iconStatus)}
                />
              )}
              {icon && !iconStatus && (
                <AstroIcon
                  name={icon as AstroIconName}
                  size={headingConfig.iconSizeCompact}
                  color={tokens.colors.text.tertiary}
                />
              )}
              {props.title && (
                <span
                  style={{
                    fontSize: chartTitleFontSize,
                    fontWeight: headingConfig.titleFontWeight,
                    color: tokens.colors.text.primary,
                  }}
                >
                  {props.title}
                </span>
              )}
            </div>
          )}

          {/* Header - Non-transparent themes (Zen/Astro): same top-left layout, no box/border */}
          {hasHeader && !isTransparentTheme && (
            <div
              title={statusMessage}
              style={{
                position: "absolute",
                top: 6,
                left: 8,
                display: "flex",
                alignItems: "center",
                gap: headingConfig.gap,
                zIndex: 10,
              }}
            >
              {icon && iconStatus && (
                <HeaderIconWithStatus
                  icon={icon as AstroIconName}
                  size={headingConfig.iconSizeCompact}
                  status={iconStatus}
                  statusColor={getStatusColor(iconStatus)}
                />
              )}
              {icon && !iconStatus && (
                <AstroIcon
                  name={icon as AstroIconName}
                  size={headingConfig.iconSizeCompact}
                  color={tokens.colors.text.tertiary}
                />
              )}
              {props.title && (
                <span
                  style={{
                    fontSize: chartTitleFontSize,
                    fontWeight: headingConfig.titleFontWeight,
                    color: tokens.colors.text.primary,
                  }}
                >
                  {props.title}
                </span>
              )}
            </div>
          )}
          {/* SVG renderer by default for sharp text; canvas is forced when echarts-gl 3D series are present (gl requires canvas) */}
          <ReactEChartsCore
            ref={chartRef}
            echarts={echarts}
            option={options}
            opts={{
              renderer: props.renderer ?? (needs3DCanvas ? "canvas" : "svg"),
              ...((props.renderer === "canvas" || needs3DCanvas) &&
                typeof window !== "undefined" && {
                  devicePixelRatio: window.devicePixelRatio || 1,
                }),
            }}
            style={{ width: "100%", height: "100%" }}
            notMerge={true}
            lazyUpdate={true}
            showLoading={loading}
            loadingOption={{
              text: "Loading...",
              color: tokens.colors.accent.primary,
              textColor: tokens.colors.text.primary,
              // Hex-alpha suffixing only works on hex tokens; surface is an
              // rgba() string in several themes, so use the overlay token,
              // which is defined with mask-appropriate alpha in every theme.
              maskColor: isTransparentTheme
                ? "rgba(10, 15, 25, 0.6)"
                : tokens.colors.background.overlay,
            }}
            onEvents={handleEvents}
            onChartReady={handleChartReady}
          />
          {/* Chart toolbar - compact, positioned top right */}
          <div
            style={{
              position: "absolute",
              right: 8,
              top: 6,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {/* Info tooltip button */}
            {infoTooltip && (
              <div ref={infoTooltipRef} style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() => setInfoTooltipOpen((v) => !v)}
                  aria-label="Chart information"
                  aria-expanded={infoTooltipOpen}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 20,
                    height: 20,
                    padding: 4,
                    border: `1px solid ${tokens.colors.border.default}`,
                    borderRadius: "50%",
                    background: infoTooltipOpen
                      ? tokens.colors.accent.primary
                      : isTransparentTheme
                        ? "rgba(10, 15, 25, 0.5)"
                        : tokens.colors.background.surface,
                    color: infoTooltipOpen
                      ? tokens.colors.background.base
                      : tokens.colors.text.secondary,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                >
                  {infoTooltip.icon || (
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                    </svg>
                  )}
                </button>
                {infoTooltipOpen && (
                  <div
                    role="tooltip"
                    style={{
                      position: "absolute",
                      right: 0,
                      top: "100%",
                      marginTop: 8,
                      minWidth: 200,
                      maxWidth: infoTooltip.maxWidth || 300,
                      padding: "12px 16px",
                      background: isTransparentTheme
                        ? "rgba(10, 15, 25, 0.85)"
                        : tokens.colors.background.surface,
                      backdropFilter: isTransparentTheme
                        ? "blur(12px)"
                        : undefined,
                      border: `1px solid ${tokens.colors.border.default}`,
                      borderRadius: tokens.borderRadius.md,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
                      color: tokens.colors.text.primary,
                      fontFamily: tokens.typography.fontFamily.primary,
                      fontSize: tokens.typography.fontSize.sm,
                      lineHeight: 1.5,
                      zIndex: 20,
                    }}
                  >
                    {infoTooltip.content}
                  </div>
                )}
              </div>
            )}

            {/* Export dropdown - compact icon only */}
            {useCustomExportDropdown && (
              <div ref={exportMenuRef} style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() => setExportMenuOpen((v) => !v)}
                  aria-haspopup="true"
                  aria-expanded={exportMenuOpen}
                  aria-label="Save chart as"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 20,
                    height: 20,
                    padding: 4,
                    border: `1px solid ${tokens.colors.border.default}`,
                    borderRadius: "50%",
                    background: isTransparentTheme
                      ? "rgba(10, 15, 25, 0.5)"
                      : tokens.colors.background.surface,
                    color: tokens.colors.text.secondary,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                >
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </button>
                {exportMenuOpen && (
                  <div
                    role="menu"
                    style={{
                      position: "absolute",
                      right: 0,
                      top: "100%",
                      marginTop: 4,
                      minWidth: 120,
                      padding: 4,
                      background: tokens.colors.background.surface,
                      border: `1px solid ${tokens.colors.border.default}`,
                      borderRadius: tokens.borderRadius.md,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                    }}
                  >
                    {exportImageFormats.map((f) => (
                      <button
                        key={f}
                        type="button"
                        role="menuitem"
                        onClick={() =>
                          handleExportFormat(
                            f === "jpg"
                              ? "jpeg"
                              : (f as "png" | "jpeg" | "svg"),
                          )
                        }
                        style={{
                          display: "block",
                          width: "100%",
                          padding: "8px 12px",
                          border: "none",
                          borderRadius: tokens.borderRadius.sm,
                          background: "transparent",
                          color: tokens.colors.text.primary,
                          fontFamily: tokens.typography.fontFamily.primary,
                          fontSize: tokens.typography.fontSize.sm,
                          textAlign: "left",
                          cursor: "pointer",
                        }}
                      >
                        {f.toUpperCase()}
                      </button>
                    ))}
                    {hasCsv && (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => handleExportFormat("csv")}
                        style={{
                          display: "block",
                          width: "100%",
                          padding: "8px 12px",
                          border: "none",
                          borderRadius: tokens.borderRadius.sm,
                          background: "transparent",
                          color: tokens.colors.text.primary,
                          fontFamily: tokens.typography.fontFamily.primary,
                          fontSize: tokens.typography.fontSize.sm,
                          textAlign: "left",
                          cursor: "pointer",
                        }}
                      >
                        CSV
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    },
  ),
);

export default AstroChart;
